// components/IntelligentExcelImport.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Upload, Steps, message, Select, Row, Col, Alert, Progress, Table, Tag, Spin, Checkbox, Tooltip, Input } from 'antd';
import { FileExcelOutlined, UploadOutlined, CheckCircleOutlined, RobotOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import axios from '../../utils/axios';

const { Step } = Steps;
const { Option } = Select;

const IntelligentExcelImport = ({ 
    visible, 
    onClose, 
    onImport,
    clients,
    items,
    stationPoints,
    functionalStatus,
    assetCatId 
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [excelData, setExcelData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [mappingSuggestions, setMappingSuggestions] = useState({});
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const [defaultClientId, setDefaultClientId] = useState(null);
    const [defaultStationPointId, setDefaultStationPointId] = useState(null);
    // Location TBC — vehicles imported without confirmed location (pending ground verification)
    const [locationTBC, setLocationTBC] = useState(false);

    // Vehicle name normalisation — step 1.5 for asset_cat_id === '8'
    const [vehicleNameMap, setVehicleNameMap] = useState([]); // [{rawKey, make, model, type, cleanName, item_id|null}]
    const [showNormalise, setShowNormalise] = useState(false);
    const [confidenceScoreMap, setConfidenceScoreMap] = useState({});

    // AI-powered column mapping suggestions
    const suggestColumnMapping = (excelHeaders) => {
        const suggestions = {};
        const confidenceScores = {};
        
        const mappingRules = [
            // Facility/Client patterns
            {
                field: 'client_id',
                patterns: ['facility', 'client', 'site', 'location', 'hospital', 'clinic'],
                transform: (value) => {
                    const facility = clients.find(c => 
                        c.name.toLowerCase().includes(value.toLowerCase()) ||
                        c.site_code?.toLowerCase() === value.toLowerCase()
                    );
                    return facility?.id;
                }
            },
            // Asset Name / Make patterns — for vehicles, Make column = item name
            {
                field: 'item_id',
                patterns: ['asset', 'item', 'equipment', 'name', 'description', 'product', 'make', 'manufacturer', 'brand'],
                transform: (value) => {
                    const item = items.find(i => 
                        i.name.toLowerCase().includes(value.toLowerCase())
                    );
                    return item?.id;
                }
            },
            // Serial Number patterns
            {
                field: 'serial_no',
                patterns: ['serial', 'registration', 'plate', 'number', 'identifier', 'reg', 's/n', 'sn'],
                transform: (value) => value?.toString().toUpperCase()
            },
            // Status patterns
            {
                field: 'functional_status',
                patterns: ['status', 'condition', 'state', 'functional'],
                transform: (value) => {
                    const status = functionalStatus.find(fs => 
                        fs.name.toLowerCase() === value.toLowerCase()
                    );
                    return status?.id || '1';
                }
            },
            // Quantity patterns
            {
                field: 'qty',
                patterns: ['quantity', 'qty', 'amount', 'count', 'stock'],
                transform: (value) => parseInt(value) || 1
            },
            // Cost patterns
            {
                field: 'cost',
                patterns: ['cost', 'price', 'value', 'amount', 'szl', 'e'],
                transform: (value) => parseFloat(value) || 0
            },
            // Location patterns
            {
                field: 'station_point_ids',
                patterns: ['location', 'station', 'point', 'ward', 'department', 'room'],
                transform: (value) => {
                    const station = stationPoints.find(sp => 
                        sp.name.toLowerCase().includes(value.toLowerCase())
                    );
                    return station?.id;
                }
            },
            // Remarks patterns
            {
                field: 'eicomment',
                patterns: ['remark', 'comment', 'note', 'description', 'detail'],
                transform: (value) => value?.toString()
            },
            // Date patterns
            {
                field: 'installation_date',
                patterns: ['install', 'date', 'installed', 'installation', 'purchase'],
                transform: (value) => value
            }
        ];
        
        // Vehicle-specific patterns (HEALTH Vehicle Registry columns included)
        if (assetCatId === '8') {
            mappingRules.push(
                // Registration plate
                {
                    field: 'serial_no',
                    patterns: ['registration', 'reg', 'plate', 'reg no', 'rego'],
                    transform: (value) => value?.toString().toUpperCase()
                },
                // Chassis / VIN
                {
                    field: 'order_number',
                    patterns: ['chassis', 'chassis no', 'vin', 'frame', 'order'],
                    transform: (value) => value?.toString()
                },
                // Engine number — stored in eieftnumber (dedicated column)
                {
                    field: 'eieftnumber',
                    patterns: ['engine', 'engine no', 'engine number'],
                    transform: (value) => value ? value.toString().trim() : ''
                },
                // Mileage
                {
                    field: 'delivery_note_number',
                    patterns: ['mileage', 'odometer', 'km', 'kilometers'],
                    transform: (value) => value?.toString()
                },
                // Model name — stored in eimonth field (vehicle model/year field)
                {
                    field: 'eimonth',
                    patterns: ['model', 'model year', 'year', 'manufacture year'],
                    transform: (value) => value?.toString()
                },
                // Make — mapped to eircname (vehicle make/type field)
                {
                    field: 'eircname',
                    patterns: ['make', 'manufacturer', 'brand', 'maker'],
                    transform: (value) => value?.toString().toUpperCase()
                },
                // Body type — stored in subcat/description via item name
                // (handled by autoCreateVehicleItem, not a direct asset field)
                // Date Registered → procurement_date
                {
                    field: 'procurement_date',
                    patterns: ['date registered', 'registered', 'date', 'registration date', 'reg date'],
                    transform: (value) => {
                        // Handle YYYYMMDD integer format from vehicle registry
                        const str = value?.toString();
                        if (str && str.length === 8 && /^\d{8}$/.test(str)) {
                            return `${str.slice(0,4)}-${str.slice(4,6)}-${str.slice(6,8)}`;
                        }
                        return str || null;
                    }
                }
            );
        }
        
        excelHeaders.forEach(header => {
            const cleanHeader = header.toLowerCase().trim();
            let bestMatch = null;
            let highestConfidence = 0;
            
            mappingRules.forEach(rule => {
                rule.patterns.forEach(pattern => {
                    // Calculate confidence based on similarity
                    let confidence = 0;
                    
                    if (cleanHeader === pattern) {
                        confidence = 1.0; // Exact match
                    } else if (cleanHeader.includes(pattern)) {
                        confidence = 0.9; // Contains pattern
                    } else if (pattern.includes(cleanHeader)) {
                        confidence = 0.8; // Pattern contains header
                    } else {
                        // Calculate string similarity
                        const similarity = calculateStringSimilarity(cleanHeader, pattern);
                        if (similarity > 0.7) {
                            confidence = similarity * 0.7;
                        }
                    }
                    
                    if (confidence > highestConfidence) {
                        highestConfidence = confidence;
                        bestMatch = {
                            field: rule.field,
                            transform: rule.transform,
                            confidence
                        };
                    }
                });
            });
            
            if (bestMatch && bestMatch.confidence > 0.6) {
                suggestions[header] = bestMatch.field;
                confidenceScores[header] = bestMatch.confidence;
            }
        });
        
        setMappingSuggestions(suggestions);
        setColumnMapping(suggestions);
        setConfidenceScoreMap(confidenceScores);
        
        return { suggestions, confidenceScores };
    };

    const calculateStringSimilarity = (str1, str2) => {
        const longer = str1.length > str2.length ? str1 : str2;
        const shorter = str1.length > str2.length ? str2 : str1;
        
        if (longer.length === 0) return 1.0;
        
        const editDistance = levenshteinDistance(longer, shorter);
        return (longer.length - editDistance) / longer.length;
    };

    const levenshteinDistance = (str1, str2) => {
        const matrix = [];
        
        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }
        
        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }
        
        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        
        return matrix[str2.length][str1.length];
    };

    const handleFileUpload = (file) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(e.target.result, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                if (json.length < 2) {
                    message.error('Excel file must have headers and at least one data row');
                    return;
                }

                // ── Smart header row detection ────────────────────────────────
                // The HEALTH Vehicle Registry has a title row before the real headers.
                // Scan the first 10 rows to find the one that looks most like column headers:
                //   - contains known vehicle field keywords, OR
                //   - has the most non-empty cells, OR
                //   - all cells are short strings (not long descriptive text)
                const HEADER_KEYWORDS = [
                    'make', 'model', 'type', 'registration', 'reg', 'plate', 'chassis',
                    'engine', 'mileage', 'status', 'facility', 'location', 'serial',
                    'date', 'year', 'vehicle', 'description', 'item', 'name', 'no',
                    'number', 'km', 'brand', 'manufacturer', 'odometer'
                ];

                let headerRowIndex = 0;
                let bestScore = -1;

                for (let r = 0; r < Math.min(10, json.length); r++) {
                    const row = json[r] || [];
                    const nonEmpty = row.filter(c => c !== undefined && c !== null && c !== '');
                    if (nonEmpty.length === 0) continue;

                    // Score: keyword hits + non-empty cell count + short string bonus
                    let score = nonEmpty.length;
                    nonEmpty.forEach(cell => {
                        const s = cell.toString().toLowerCase().trim();
                        if (HEADER_KEYWORDS.some(kw => s.includes(kw))) score += 3;
                        if (s.length < 30) score += 1; // headers are short
                        if (s.length > 60) score -= 5; // long text = probably a title/note
                    });

                    if (score > bestScore) {
                        bestScore = score;
                        headerRowIndex = r;
                    }
                }

                const headers = (json[headerRowIndex] || []).map(h =>
                    h !== undefined && h !== null ? h.toString().trim() : ''
                ).filter(h => h !== '');

                const data = json
                    .slice(headerRowIndex + 1)
                    .filter(row => row && row.some(cell => cell !== undefined && cell !== null && cell !== ''));
                
                setHeaders(headers);
                setExcelData(data);
                
                // Auto-suggest column mapping
                const { suggestions, confidenceScores } = suggestColumnMapping(headers);
                
                const highConfidenceMappings = Object.entries(confidenceScores)
                    .filter(([_, conf]) => conf > 0.8)
                    .length;
                
                message.success(
                    `Found ${headers.length} columns. AI suggested ${highConfidenceMappings} high-confidence mappings.`
                );
                
                // Always go straight to column mapping — normalisation modal
                // is only for the Items catalogue import (Items.js), not here.
                setCurrentStep(1);
                
            } catch (error) {
                console.error('Excel parsing error:', error);
                message.error('Failed to parse Excel file');
            }
        };
        
        reader.readAsBinaryString(file);
        return false;
    };

    const handleMappingChange = (header, field) => {
        setColumnMapping(prev => ({
            ...prev,
            [header]: field
        }));
    };

    // Build the unique Make+Model+Type map from the uploaded file
    // Called after file upload when assetCatId === '8'
    const buildVehicleNameMap = (data, hdrs) => {
        const makeIdx  = hdrs.findIndex(h => /make|manufacturer|brand/i.test(h));
        const modelIdx = hdrs.findIndex(h => /^model$/i.test(h));
        const typeIdx  = hdrs.findIndex(h => /^type$|body.?type|vehicle.?type/i.test(h));

        // Same normalisation maps as processImport
        const MAKE_NORMALISE = {
            'MERCEDES BENZ':   'MERCEDES-BENZ', 'MERCEDES': 'MERCEDES-BENZ', 'MERC': 'MERCEDES-BENZ',
            'KA': 'TOYOTA', 'HILUX': 'TOYOTA', 'TOYOTA HILUX': 'TOYOTA',
            'NISSAN DISIL': 'NISSAN', 'TF185 SUZUKI': 'SUZUKI',
            'LNROVER LEYLAND': 'LAND ROVER', 'SPRITE': 'CI',
            'CAPILANO': '__SKIP__', 'KEVIN MCANDLE': '__SKIP__',
            'CARAVAN': '__SKIP__', 'MOBILE KITCHEN': '__SKIP__',
        };
        const TYPE_NORMALISE_BM = {
            'D CAB':'DOUBLE CAB','D-CAB':'DOUBLE CAB','D/C':'DOUBLE CAB','D/CAB':'DOUBLE CAB',
            'BOUBLE CAB':'DOUBLE CAB','DOUBLE CAP':'DOUBLE CAB',
            'D-CAB 4X4':'DOUBLE CAB 4X4','D/C 4X4':'DOUBLE CAB 4X4','D/CAB 4X4':'DOUBLE CAB 4X4',
            'D/C 4X2':'DOUBLE CAB 4X2','D/CAB 4X2':'DOUBLE CAB 4X2',
            'PICK UP':'PICK-UP','PICK UP 4X2':'PICK-UP 4X2','PICK UP 4X4':'PICK-UP 4X4',
            'D4D PICK UP':'PICK-UP','PICK UP D4D':'PICK-UP','LDV PICK UP':'LDV',
            '4 BY 2':'PICK-UP 4X2','4X2':'PICK-UP 4X2','4X4':'PICK-UP 4X4','BAKKIE':'PICK-UP',
            'S-C 4X4':'SINGLE CAB 4X4','S/C 4X4':'SINGLE CAB 4X4','S.CAB':'SINGLE CAB',
            'CAB':'SINGLE CAB','C-CAB':'CREW CAB',
            'S WAGON':'STATION WAGON','S-WAGON':'STATION WAGON','S/WAGON':'STATION WAGON',
            'PRADO SUV':'STATION WAGON','SUV 4X4':'SUV','S.UV.':'SUV',
            'M CYCLE':'MOTOR CYCLE','M/CYCLE':'MOTOR CYCLE','MOTO CYCLE':'MOTOR CYCLE','MOTOR BIKE':'MOTOR CYCLE',
            '14 SEATER':'MINIBUS','14 SEATER BUS':'MINIBUS','MINI BUS':'MINIBUS',
            'BUS 14 SEATER':'MINIBUS','QUANTUBUS':'MINIBUS','AMBU':'AMBULANCE',
            'BOX/CARGO TRUC':'BOX TRUCK','TRUCK BOX':'BOX TRUCK','L-TRUCK':'TRUCK',
            'FLAT':'FLAT BED','FLAT DROPSIDE':'FLAT BED','DROPSIDE FLAT':'FLAT BED','LIGHT DROPSIDE':'DROPSIDE',
            'VACCUM TANKER':'WATER TANKER','VACUUM TANKER':'WATER TANKER',
            'REFRIGERATOR':'REFRIGERATOR TRUCK','ISUZU':'TRUCK','TOYOTA':'PICK-UP',
        };

        const seen = {};
        data.forEach(row => {
            const rawMake = (row[makeIdx]  || '').toString().trim().toUpperCase();
            const model   = (row[modelIdx] || '').toString().trim().toUpperCase();
            const rawType = (row[typeIdx]  || '').toString().trim().toUpperCase();
            const type    = TYPE_NORMALISE_BM[rawType] ?? rawType;

            if (!rawMake) return; // blank make — skip

            // Junk trailer rows
            const isJunkTrailer = rawMake === 'TRAILER' &&
                (/^0+$/.test(model.replace(/\s/g, '')) || model === '');
            if (isJunkTrailer) return;

            const make = MAKE_NORMALISE[rawMake] ?? rawMake;
            if (make === '__SKIP__') return; // known unknowns

            const rawKey = `${make}||${model}||${type}`;
            if (!seen[rawKey]) {
                // Suggested clean name: Make + Model (skip chassis codes that are all-caps alphanumeric >6 chars)
                const isChassis = /^[A-Z0-9]{7,}$/.test(model);
                const cleanName = isChassis
                    ? make
                    : [make, model].filter(Boolean).join(' ').trim();
                seen[rawKey] = { rawKey, make, model, type, cleanName, item_id: null };
            }
        });
        return Object.values(seen).sort((a, b) => a.cleanName.localeCompare(b.cleanName));
    };

    // Cache for auto-created vehicle items — avoids duplicate API calls for same Make+Type
    const vehicleItemCache = {};

    const autoCreateVehicleItem = async (cleanName, _unused) => {
        // cleanName is the user-reviewed normalised item name e.g. "TOYOTA HILUX"
        const key = cleanName.toUpperCase();
        if (vehicleItemCache[key]) return vehicleItemCache[key];

        try {
            const res = await axios.post('asset/autoCreateVehicleItem', { make: cleanName, type: '' });
            const json = res.data;
            if (json.item_id) {
                vehicleItemCache[key] = json.item_id;
                return json.item_id;
            }
        } catch (e) {
            console.error('autoCreateVehicleItem failed:', e);
        }
        return null;
    };

    const processImport = async () => {
        setImporting(true);
        setCurrentStep(2);
        
        try {
            const processedData = [];
            const errors = [];

            // For vehicle imports, find which header columns hold Make and Type
            // The HEALTH Vehicle Registry uses: Registration, Engine No, Chassis No,
            // Date Registered, Model (= model name), Make (= manufacturer), Type (= body type)
            const makeColIndex = headers.findIndex(h =>
                /make|manufacturer|brand/i.test(h)
            );
            const typeColIndex = headers.findIndex(h =>
                /^type$|body.?type|vehicle.?type/i.test(h)
            );
            const modelColIndex = headers.findIndex(h =>
                /^model$/i.test(h)
            );

            // ── Normalisation maps — defined once, used throughout the loop ──
            const MAKE_NORMALISE = {
                'MERCEDES BENZ':   'MERCEDES-BENZ',
                'MERCEDES':        'MERCEDES-BENZ',
                'MERC':            'MERCEDES-BENZ',
                'KA':              'TOYOTA',
                'HILUX':           'TOYOTA',
                'TOYOTA HILUX':    'TOYOTA',
                'NISSAN DISIL':    'NISSAN',
                'TF185 SUZUKI':    'SUZUKI',
                'LNROVER LEYLAND': 'LAND ROVER',
                'SPRITE':          'CI',
                'CAPILANO':        '__SKIP__',
                'KEVIN MCANDLE':   '__SKIP__',
                'CARAVAN':         '__SKIP__',
                'MOBILE KITCHEN':  '__SKIP__',
            };
            const TYPE_NORMALISE = {
                'D CAB':'DOUBLE CAB','D-CAB':'DOUBLE CAB','D/C':'DOUBLE CAB','D/CAB':'DOUBLE CAB',
                'BOUBLE CAB':'DOUBLE CAB','DOUBLE CAP':'DOUBLE CAB',
                'D-CAB 4X4':'DOUBLE CAB 4X4','D/C 4X4':'DOUBLE CAB 4X4','D/CAB 4X4':'DOUBLE CAB 4X4',
                'D/C 4X2':'DOUBLE CAB 4X2','D/CAB 4X2':'DOUBLE CAB 4X2',
                'PICK UP':'PICK-UP','PICK UP 4X2':'PICK-UP 4X2','PICK UP 4X4':'PICK-UP 4X4',
                'D4D PICK UP':'PICK-UP','PICK UP D4D':'PICK-UP','LDV PICK UP':'LDV',
                '4 BY 2':'PICK-UP 4X2','4X2':'PICK-UP 4X2','4X4':'PICK-UP 4X4','BAKKIE':'PICK-UP',
                'S-C 4X4':'SINGLE CAB 4X4','S/C 4X4':'SINGLE CAB 4X4','S.CAB':'SINGLE CAB',
                'CAB':'SINGLE CAB','C-CAB':'CREW CAB',
                'S WAGON':'STATION WAGON','S-WAGON':'STATION WAGON','S/WAGON':'STATION WAGON',
                'PRADO SUV':'STATION WAGON','SUV 4X4':'SUV','S.UV.':'SUV',
                'M CYCLE':'MOTOR CYCLE','M/CYCLE':'MOTOR CYCLE','MOTO CYCLE':'MOTOR CYCLE','MOTOR BIKE':'MOTOR CYCLE',
                '14 SEATER':'MINIBUS','14 SEATER BUS':'MINIBUS','MINI BUS':'MINIBUS',
                'BUS 14 SEATER':'MINIBUS','QUANTUBUS':'MINIBUS','AMBU':'AMBULANCE',
                'BOX/CARGO TRUC':'BOX TRUCK','TRUCK BOX':'BOX TRUCK','L-TRUCK':'TRUCK',
                'FLAT':'FLAT BED','FLAT DROPSIDE':'FLAT BED','DROPSIDE FLAT':'FLAT BED','LIGHT DROPSIDE':'DROPSIDE',
                'VACCUM TANKER':'WATER TANKER','VACUUM TANKER':'WATER TANKER',
                'REFRIGERATOR':'REFRIGERATOR TRUCK','ISUZU':'TRUCK','TOYOTA':'PICK-UP',
                '000':'__SKIP__','05.5':'__SKIP__','1324':'__SKIP__','OTHER MACHINERY':'__SKIP__',
            };
            // ─────────────────────────────────────────────────────────────────

            for (let index = 0; index < excelData.length; index++) {
                const row = excelData[index];
                try {
                    const assetData = {
                        client_id: null,
                        item_id: null,
                        functional_status: null, // will be resolved to TBC below
                        qty: 1,
                        cost: 0,
                        station_point_ids: [],
                        serial_no: '',
                        eicomment: '',
                        eieftnumber: '',
                        isdonated: 0
                    };
                    
                    headers.forEach((header, colIndex) => {
                        const field = columnMapping[header];
                        if (field && row[colIndex] !== undefined && row[colIndex] !== '') {
                            const value = row[colIndex];
                            
                            switch (field) {
                                case 'client_id': {
                                    const facility = clients.find(c => 
                                        c.name.toLowerCase().includes(value.toString().toLowerCase())
                                    );
                                    assetData[field] = facility?.id;
                                    break;
                                }
                                case 'item_id': {
                                    // Store raw make value — resolved below using type/subcat
                                    assetData._itemRaw = value.toString().trim();
                                    // item_id resolved after all columns mapped (needs type col)
                                    break;
                                }
                                case 'station_point_ids': {
                                    const station = stationPoints.find(sp => 
                                        sp.name.toLowerCase().includes(value.toString().toLowerCase())
                                    );
                                    if (station) {
                                        assetData[field] = [station.id];
                                    }
                                    break;
                                }
                                case 'functional_status': {
                                    const status = functionalStatus.find(fs => 
                                        fs.name.toLowerCase() === value.toString().toLowerCase()
                                    );
                                    assetData[field] = status?.id || '1';
                                    break;
                                }
                                case 'qty':
                                case 'cost':
                                    assetData[field] = parseFloat(value) || (field === 'qty' ? 1 : 0);
                                    break;
                                case 'procurement_date':
                                case 'installation_date': {
                                    // Normalise YYYYMMDD integer → YYYY-MM-DD string
                                    const dStr = value?.toString().trim().replace(/-/g, '');
                                    if (dStr && dStr.length === 8 && /^\d{8}$/.test(dStr)) {
                                        assetData[field] = `${dStr.slice(0,4)}-${dStr.slice(4,6)}-${dStr.slice(6,8)}`;
                                    } else if (dStr && dStr.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dStr)) {
                                        assetData[field] = dStr; // already correct
                                    } else {
                                        // Try parsing as a general date string
                                        const parsed = new Date(value);
                                        assetData[field] = isNaN(parsed.getTime())
                                            ? null
                                            : parsed.toISOString().slice(0, 10);
                                    }
                                    break;
                                }
                                default:
                                    assetData[field] = value;
                            }
                        }
                    });

                    // Apply facility default
                    if (!assetData.client_id && defaultClientId) {
                        assetData.client_id = defaultClientId;
                    }

                    // Default functional_status to TBC for vehicles if not set from file
                    if (!assetData.functional_status) {
                        const tbcStatus = functionalStatus.find(fs =>
                            fs.name.toLowerCase() === 'tbc' ||
                            fs.name.toLowerCase().includes('to be confirmed') ||
                            fs.name.toLowerCase().includes('unknown')
                        );
                        // Use TBC id if found, otherwise fall back to first status or '1'
                        assetData.functional_status = tbcStatus?.id || (functionalStatus[0]?.id) || '1';
                    }

                    // ── Auto-assign vehicle station point ────────────────────
                    // If no location resolved from file, find the best vehicle
                    // station point that is:
                    //   1. Linked to the asset's facility (client_id)
                    //   2. Tagged for vehicles (asset_cat_ids contains '8')
                    // Falls back to defaultStationPointId, then first vehicle SP globally.
                    if (!assetData.station_point_ids || assetData.station_point_ids.length === 0) {
                        const facilityId = assetData.client_id || defaultClientId;

                        // Vehicle station points: asset_cat_ids contains '8'
                        const isVehicleSP = (sp) =>
                            sp.asset_cat_ids &&
                            sp.asset_cat_ids.toString().split(',').map(s => s.trim()).includes('8');

                        // Prefer one linked to this facility AND tagged as vehicle
                        let autoSP = stationPoints.find(sp =>
                            isVehicleSP(sp) &&
                            sp.client_ids && sp.client_ids.includes(facilityId)
                        );

                        // Fallback: any vehicle station point for this facility
                        if (!autoSP) {
                            autoSP = stationPoints.find(sp =>
                                sp.client_ids && sp.client_ids.includes(facilityId)
                            );
                        }

                        // Fallback: default location selected on step 1
                        if (!autoSP && defaultStationPointId) {
                            autoSP = stationPoints.find(sp => sp.id === defaultStationPointId);
                        }

                        // Fallback: first vehicle station point globally
                        if (!autoSP) {
                            autoSP = stationPoints.find(sp => isVehicleSP(sp));
                        }

                        if (autoSP) {
                            assetData.station_point_ids = [autoSP.id];
                        }
                    }

                    // ── INTELLIGENT ITEM RESOLUTION (Make + Type → item_id) ──
                    // For vehicles: items table has one row per Make+SubCat combination
                    // e.g. TOYOTA [AMBULANCE], TOYOTA [PICK-UP], TOYOTA [DOUBLE CAB]
                    // We must match BOTH the make AND the sub-category (type) to pick correctly
                    if (!assetData.item_id && assetCatId === '8' && assetData._itemRaw) {
                        const rawMakeForRes = assetData._itemRaw.toUpperCase();
                        const normMakeForRes = MAKE_NORMALISE[rawMakeForRes] ?? rawMakeForRes;

                        // Get the normalised type for this row
                        const rawTypeForRes = typeColIndex >= 0
                            ? (row[typeColIndex] || '').toString().trim().toUpperCase()
                            : '';
                        const normTypeForRes = TYPE_NORMALISE[rawTypeForRes] ?? rawTypeForRes;

                        // Items are named like "TOYOTA" but display as "TOYOTA [AMBULANCE]"
                        // The items list has item.subcat = 'AMBULANCE' etc.
                        // Strategy:
                        //   1. Exact make + exact subcat match
                        //   2. Exact make + partial subcat match
                        //   3. Exact make only (fallback — least preferred)

                        const makeItems = items.filter(i =>
                            i.name.toLowerCase() === normMakeForRes.toLowerCase()
                        );

                        let resolvedItem = null;

                        if (makeItems.length === 1) {
                            // Only one item for this make — use it
                            resolvedItem = makeItems[0];
                        } else if (makeItems.length > 1 && normTypeForRes) {
                            // Multiple items for this make — narrow by sub-category
                            // Extract sub-category name from item label e.g. "TOYOTA [AMBULANCE]"
                            const subcatFromType = normTypeForRes.toUpperCase();

                            // 1. Exact subcat match
                            resolvedItem = makeItems.find(i => {
                                const sc = (i.subcat || i.item_full_name || '').toUpperCase();
                                return sc.includes(subcatFromType) || subcatFromType.includes(sc.replace(/.*\[|\].*/g,'').trim());
                            });

                            // 2. If still no match, try the bracket notation in item_full_name
                            if (!resolvedItem) {
                                resolvedItem = makeItems.find(i => {
                                    const bracket = (i.item_full_name || '').match(/\[(.+?)\]/)?.[1]?.toUpperCase() || '';
                                    return bracket && subcatFromType.startsWith(bracket.split(' ')[0]);
                                });
                            }

                            // 3. Fallback: pick first item for this make
                            // (better than picking from all items)
                            if (!resolvedItem) resolvedItem = makeItems[0];
                        } else if (makeItems.length === 0) {
                            // No exact make match — try partial
                            resolvedItem = items.find(i =>
                                i.name.toLowerCase().includes(normMakeForRes.toLowerCase()) ||
                                normMakeForRes.toLowerCase().includes(i.name.toLowerCase())
                            );
                        }

                        if (resolvedItem) {
                            assetData.item_id = resolvedItem.id;
                        }
                    }
                    // ── VEHICLE ITEM AUTO-CREATE ─────────────────────────────
                    if (!assetData.item_id && assetCatId === '8') {
                        let rawMake  = makeColIndex  >= 0 ? (row[makeColIndex]  || '').toString().trim().toUpperCase() : '';
                        const model = modelColIndex >= 0 ? (row[modelColIndex] || '').toString().trim().toUpperCase() : '';
                        const type  = typeColIndex  >= 0 ? (row[typeColIndex]  || '').toString().trim().toUpperCase() : '';

                        // MAKE_NORMALISE and TYPE_NORMALISE defined above the for loop
                        // Junk rows: make=TRAILER + model is all zeros or empty
                        const isJunkTrailer = rawMake === 'TRAILER' &&
                            /^0+$/.test(model.replace(/\s/g, '')) || model === '';

                        if (isJunkTrailer) rawMake = '__SKIP__';

                        const normalisedMake = MAKE_NORMALISE[rawMake] ?? rawMake;
                        // ─────────────────────────────────────────────────────

                        // Silent skip — these rows have no valid item and are junk/unknown
                        if (normalisedMake === '__SKIP__' || !normalisedMake) {
                            continue; // skip without pushing an error
                        }

                        // Rebuild rawKey with normalised make for vehicleNameMap lookup
                        const rawKey = `${normalisedMake}||${model}||${type}`;

                        // Also try original key in case vehicleNameMap was built before normalisation
                        const originalKey = `${rawMake}||${model}||${type}`;

                        const entry = vehicleNameMap.find(e =>
                            e.rawKey === rawKey || e.rawKey === originalKey
                        );

                        if (entry) {
                            if (entry.item_id) {
                                assetData.item_id = entry.item_id;
                            } else {
                                const createdId = await autoCreateVehicleItem(entry.cleanName, '');
                                if (createdId) {
                                    entry.item_id = createdId;
                                    assetData.item_id = createdId;
                                }
                            }
                        } else {
                            // vehicleNameMap miss — try direct item lookup with normalised make
                            const directItem = items.find(i =>
                                i.name.toLowerCase() === normalisedMake.toLowerCase()
                            ) || items.find(i =>
                                i.name.toLowerCase().includes(normalisedMake.toLowerCase()) ||
                                normalisedMake.toLowerCase().includes(i.name.toLowerCase())
                            );
                            if (directItem) {
                                assetData.item_id = directItem.id;
                            } else {
                                // Auto-create with normalised make name
                                const createdId = await autoCreateVehicleItem(normalisedMake, '');
                                if (createdId) assetData.item_id = createdId;
                            }
                        }
                    }
                    // ────────────────────────────────────────────────────────

                    // Validation
                    if (!assetData.client_id) {
                        errors.push(`Row ${index + 2}: Missing or invalid facility`);
                        continue;
                    }
                    if (!assetData.item_id) {
                        errors.push(`Row ${index + 2}: Could not resolve asset — check Make/Type columns`);
                        continue;
                    }
                    // Location: required for non-vehicles; vehicles get auto-assigned above
                    if (assetCatId !== '8') {
                        if (!assetData.station_point_ids || assetData.station_point_ids.length === 0) {
                            errors.push(`Row ${index + 2}: Missing or invalid location`);
                            continue;
                        }
                    }

                    // All imported vehicles: pending verification + mark source as excel import
                    if (assetCatId === '8') {
                        assetData.verification_status = 'pending';
                        assetData.data_source = 'excel_import';
                    }

                    // Clean up internal-only fields before handing to parent
                    delete assetData._itemRaw;
                    
                    processedData.push(assetData);
                    
                } catch (error) {
                    errors.push(`Row ${index + 2}: ${error.message}`);
                }
            }
            
            setImportResults({
                success: processedData.length,
                failed: errors.length,
                errors: errors
            });
            
            if (processedData.length > 0) {
                message.success(`Processed ${processedData.length} assets successfully`);
                onImport(processedData);
            }
            
            if (errors.length > 0) {
                message.warning(`${errors.length} rows had errors`);
            }
            
            setCurrentStep(3);
            
        } catch (error) {
            console.error('Import processing error:', error);
            message.error('Failed to process import');
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setCurrentStep(0);
        setExcelData([]);
        setHeaders([]);
        setColumnMapping({});
        setMappingSuggestions({});
        setImportResults(null);
        setDefaultClientId(null);
        setDefaultStationPointId(null);
        setVehicleNameMap([]);
        setShowNormalise(false);
        setLocationTBC(false);
        setConfidenceScoreMap({});
        onClose();
    };

    const dbFields = [
        { value: 'client_id', label: 'Facility Name' },
        { value: 'item_id', label: 'Item Name (Make)' },
        { value: 'serial_no', label: 'Registration Plate' },
        { value: 'functional_status', label: 'Vehicle Status' },
        { value: 'station_point_ids', label: 'Current Location' },
        { value: 'qty', label: 'Quantity' },
        { value: 'cost', label: 'Cost (SZL)' },
        { value: 'installation_date', label: 'Date Registered' },
        ...(assetCatId === '8' ? [
            { value: 'eieftnumber', label: 'Engine Number' },
            { value: 'order_number', label: 'Chassis Number' },
            { value: 'delivery_note_number', label: 'Mileage' },
            { value: 'eimonth', label: 'Model / Year' },
            { value: 'eircname', label: 'Make (Manufacturer)' },
            { value: 'procurement_date', label: 'Date Registered' },
        ] : [
            { value: 'eicomment', label: 'Remarks' },
        ])
    ];

    const getConfidenceColor = (confidence) => {
        if (confidence > 0.9) return 'success';
        if (confidence > 0.7) return 'processing';
        return 'warning';
    };

    return (
        <>
        <Modal
            title={<><RobotOutlined /> Smart Excel Import</>}
            open={visible}
            visible={visible}
            onCancel={handleClose}
            width={900}
            zIndex={1100}
            destroyOnClose
            footer={
                currentStep === 0 ? null :
                currentStep === 1 ? [
                    <Button key="back" onClick={handleClose}>Cancel</Button>,
                    <Button 
                        key="next" 
                        type="primary" 
                        onClick={processImport}
                        disabled={Object.keys(columnMapping).length === 0}
                    >
                        Process Import
                    </Button>
                ] :
                currentStep === 2 ? null :
                [
                    <Button key="close" type="primary" onClick={handleClose}>
                        Close
                    </Button>
                ]
            }
        >
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                <Step title="Upload" icon={<UploadOutlined />} />
                <Step title="Map Columns" icon={<RobotOutlined />} />
                <Step title="Process" />
                <Step title="Complete" icon={<CheckCircleOutlined />} />
            </Steps>

            {/* Step 1: Upload */}
            {currentStep === 0 && (
                <div style={{ padding: '24px 0' }}>

                    {/* Default facility selector — required when file has no Facility column */}
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 500, marginBottom: 6 }}>
                            Default Facility <span style={{ color: '#f5222d' }}>*</span>
                            <span style={{ fontWeight: 400, color: '#666', marginLeft: 8, fontSize: 12 }}>
                                Applied to all rows that don&apos;t have a facility column
                            </span>
                        </div>
                        <Select
                            showSearch
                            placeholder="Select the facility for this import batch"
                            style={{ width: '100%' }}
                            value={defaultClientId}
                            onChange={setDefaultClientId}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {clients.map(c => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                            ))}
                        </Select>
                    </div>

                    {/* Default location selector — for vehicles shows vehicle station points only */}
                    {defaultClientId && (
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontWeight: 500, marginBottom: 6 }}>
                                {assetCatId === '8' ? 'Default Vehicle Station Point' : 'Default Location'}
                                <span style={{ fontWeight: 400, color: '#666', marginLeft: 8, fontSize: 12 }}>
                                    {assetCatId === '8'
                                        ? 'Auto-assigned to all vehicles — data collectors will update on the ground'
                                        : 'Applied to all rows that don\'t have a location column'}
                                </span>
                            </div>
                            <Select
                                showSearch
                                allowClear
                                placeholder="Select default location (optional)"
                                style={{ width: '100%' }}
                                value={defaultStationPointId}
                                onChange={setDefaultStationPointId}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {stationPoints
                                    .filter(sp => {
                                        const linkedToFacility = sp.client_ids && sp.client_ids.includes(defaultClientId);
                                        if (assetCatId === '8') {
                                            // For vehicles: only show vehicle station points (asset_cat_ids contains '8')
                                            const isVehicleSP = sp.asset_cat_ids &&
                                                sp.asset_cat_ids.toString().split(',').map(s => s.trim()).includes('8');
                                            return linkedToFacility && isVehicleSP;
                                        }
                                        // For other categories: show non-vehicle station points
                                        return linkedToFacility && (!sp.asset_cat_ids || sp.asset_cat_ids === '');
                                    })
                                    .map(sp => (
                                        <Option key={sp.id} value={sp.id}>{sp.name}</Option>
                                    ))
                                }
                            </Select>
                        </div>
                    )}

                    {/* Vehicle import info banner */}
                    {assetCatId === '8' && (
                        <div style={{
                            marginBottom: 20,
                            padding: '12px 16px',
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 6,
                        }}>
                            <div style={{ fontWeight: 500, marginBottom: 4, color: '#389e0d' }}>
                                🚗 Vehicle Import — Pending Verification
                            </div>
                            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                                All imported vehicles will be automatically assigned to the facility's
                                vehicle station point and stamped <strong>Pending Verification</strong>.
                                Data collectors will confirm the exact location, status and mileage
                                during the ground verification exercise.
                            </div>
                        </div>
                    )}

                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <Upload
                            accept=".xlsx,.xls"
                            beforeUpload={handleFileUpload}
                            showUploadList={false}
                            disabled={!defaultClientId}
                        >
                            <Button 
                                icon={<FileExcelOutlined />} 
                                size="large"
                                type="primary"
                                disabled={!defaultClientId}
                            >
                                Select Excel File
                            </Button>
                        </Upload>
                        {!defaultClientId && (
                            <div style={{ marginTop: 8, color: '#f5222d', fontSize: 12 }}>
                                Please select a default facility before uploading
                            </div>
                        )}
                    </div>
                    
                    <Alert
                        message="Import Notes"
                        description={
                            <ul style={{ textAlign: 'left', marginTop: 8, paddingLeft: 20 }}>
                                <li>The HEALTH Vehicle Registry file is supported directly</li>
                                <li>Columns: Registration, Engine No, Chassis No, Date Registered, Model, Make, Type</li>
                                <li>All rows will be assigned to the selected default facility</li>
                                <li>Facility assignment can be updated after ground verification</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        style={{ textAlign: 'left' }}
                    />
                </div>
            )}

            {/* Step 2: Column Mapping */}
            {currentStep === 1 && (
                <div>
                    <Alert
                        message={
                            <span>
                                <RobotOutlined style={{ marginRight: 8 }} />
                                AI has analyzed your Excel file and suggested column mappings
                            </span>
                        }
                        description={`Found ${headers.length} columns in your file. Review and adjust the mappings below.`}
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        {headers.map((header, index) => {
                            const suggestion = mappingSuggestions[header];
                            // Use the real confidence score stored during suggestColumnMapping
                            const allScores = Object.fromEntries(
                                Object.entries(mappingSuggestions).map(([h, f]) => [h, f])
                            );
                            // Retrieve real score — stored in a separate ref below
                            const confidence = suggestion ? (confidenceScoreMap[header] || 0.75) : 0;
                            
                            return (
                                <Row key={index} gutter={16} style={{ marginBottom: 12, alignItems: 'center' }}>
                                    <Col span={8}>
                                        <div style={{ fontWeight: 'bold' }}>{header}</div>
                                        <div style={{ fontSize: 11, color: '#666' }}>
                                            Sample: {excelData[0]?.[index]?.toString().substring(0, 30)}...
                                        </div>
                                    </Col>
                                    
                                    <Col span={2} style={{ textAlign: 'center' }}>→</Col>
                                    
                                    <Col span={10}>
                                        <Select
                                            placeholder="Select field"
                                            value={columnMapping[header]}
                                            onChange={(value) => handleMappingChange(header, value)}
                                            style={{ width: '100%' }}
                                            showSearch
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            <Option value="">-- Skip this column --</Option>
                                            {dbFields.map(field => (
                                                <Option key={field.value} value={field.value}>
                                                    {field.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    
                                    <Col span={4}>
                                        {suggestion && (
                                            <Tag color={getConfidenceColor(confidence)}>
                                                AI: {(confidence * 100).toFixed(0)}%
                                            </Tag>
                                        )}
                                    </Col>
                                </Row>
                            );
                        })}
                    </div>
                    
                    <Alert
                        message="Mapping Tips"
                        description={
                            <ul style={{ marginTop: 8, paddingLeft: 20, marginBottom: 0 }}>
                                <li>Required fields: Facility Name, Asset Name, Location</li>
                                <li>The system will attempt fuzzy matching for names</li>
                                <li>Date fields should be in YYYY-MM-DD format</li>
                            </ul>
                        }
                        type="info"
                        style={{ marginTop: 16 }}
                    />
                </div>
            )}

            {/* Step 3: Processing */}
            {currentStep === 2 && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 24, fontSize: 16 }}>
                        Processing {excelData.length} assets...
                    </div>
                    <Progress 
                        percent={50} 
                        status="active"
                        style={{ marginTop: 16 }}
                    />
                </div>
            )}

            {/* Step 4: Results */}
            {currentStep === 3 && importResults && (
                <div>
                    <Alert
                        message="Import Complete"
                        description={
                            <div>
                                <p><strong>Successfully imported:</strong> {importResults.success} assets</p>
                                {importResults.failed > 0 && (
                                    <p><strong>Failed:</strong> {importResults.failed} rows</p>
                                )}
                            </div>
                        }
                        type={importResults.failed === 0 ? 'success' : 'warning'}
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    
                    {importResults.errors.length > 0 && (
                        <div>
                            <h4>Errors:</h4>
                            <div style={{ 
                                maxHeight: 300, 
                                overflowY: 'auto',
                                background: '#fff2f0',
                                padding: 12,
                                borderRadius: 4
                            }}>
                                {importResults.errors.map((error, i) => (
                                    <div key={i} style={{ 
                                        marginBottom: 8,
                                        color: '#cf1322',
                                        fontSize: 12
                                    }}>
                                        • {error}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Modal>

            {/* Vehicle name normalisation review — shown between upload and column mapping */}
            {assetCatId === '8' && (
                <VehicleNormaliseModal
                    visible={showNormalise}
                    nameMap={vehicleNameMap}
                    items={items}
                    onConfirm={(confirmedMap) => {
                        setVehicleNameMap(confirmedMap);
                        setShowNormalise(false);
                        setCurrentStep(1);
                    }}
                    onCancel={() => {
                        setShowNormalise(false);
                        setExcelData([]);
                        setHeaders([]);
                    }}
                />
            )}
        </>
    );
};


// ── VehicleNormaliseModal ────────────────────────────────────────────────────
// Shown between file upload and column mapping for vehicle imports.
// Lets the user review and clean the item names that will be auto-created.
const VehicleNormaliseModal = ({ visible, nameMap, items, onConfirm, onCancel }) => {
    const [localMap, setLocalMap] = useState([]);

    useEffect(() => {
        if (visible) setLocalMap(nameMap.map(e => ({ ...e })));
    }, [visible, nameMap]);

    const updateName = (rawKey, newName) => {
        setLocalMap(prev => prev.map(e =>
            e.rawKey === rawKey ? { ...e, cleanName: newName } : e
        ));
    };

    const linkExisting = (rawKey, itemId) => {
        setLocalMap(prev => prev.map(e =>
            e.rawKey === rawKey ? { ...e, item_id: itemId || null } : e
        ));
    };

    const vehicleItems = items.filter(i => i.asset_cat_id === '8');

    return (
        <Modal
            title="Review Vehicle Item Names"
            open={visible}
            width={860}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>Cancel</Button>,
                <Button
                    key="confirm"
                    type="primary"
                    onClick={() => onConfirm(localMap)}
                    disabled={localMap.some(e => !e.cleanName.trim())}
                >
                    Confirm & Continue ({localMap.length} unique vehicles)
                </Button>
            ]}
        >
            <Alert
                message="Review before import"
                description={
                    <span>
                        Found <strong>{localMap.length} unique Make+Model combinations</strong> in your file.
                        Edit the <em>Item Name</em> column to standardise spelling before they are created in the catalogue
                        (e.g. merge &quot;D CAB&quot;, &quot;D-CAB&quot; and &quot;D/CAB&quot; into one clean name).
                        If the item already exists in the system, link it using the dropdown instead.
                    </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Raw Make</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Raw Model</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Body Type</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>
                                Item Name <span style={{ color: '#f5222d' }}>*</span>
                                <span style={{ fontWeight: 400, color: '#999', marginLeft: 4 }}>(editable)</span>
                            </th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>
                                Link existing item
                                <span style={{ fontWeight: 400, color: '#999', marginLeft: 4 }}>(optional)</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {localMap.map((entry, i) => (
                            <tr key={entry.rawKey} style={{
                                borderBottom: '1px solid #f0f0f0',
                                background: i % 2 === 0 ? '#fff' : '#fafafa'
                            }}>
                                <td style={{ padding: '6px 12px', color: '#666' }}>{entry.make}</td>
                                <td style={{ padding: '6px 12px', color: '#666', maxWidth: 120, wordBreak: 'break-all' }}>{entry.model}</td>
                                <td style={{ padding: '6px 12px', color: '#666' }}>{entry.type}</td>
                                <td style={{ padding: '6px 12px' }}>
                                    <Input
                                        value={entry.cleanName}
                                        onChange={e => updateName(entry.rawKey, e.target.value)}
                                        disabled={!!entry.item_id}
                                        style={{
                                            borderColor: !entry.cleanName.trim() ? '#ff4d4f' : undefined,
                                            width: '100%'
                                        }}
                                        placeholder="Enter standardised name"
                                    />
                                </td>
                                <td style={{ padding: '6px 12px', minWidth: 200 }}>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Use existing item"
                                        style={{ width: '100%' }}
                                        value={entry.item_id || undefined}
                                        onChange={id => linkExisting(entry.rawKey, id)}
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        size="small"
                                    >
                                        {vehicleItems.map(it => (
                                            <Option key={it.id} value={it.id}>{it.item_full_name || it.name}</Option>
                                        ))}
                                    </Select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
};

export default IntelligentExcelImport;